"use client";

import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { DecisionStrings } from "@/components/tools/decision-matrix-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  analyseDecision,
  type DecisionResult,
  type WeightCheck,
} from "@/lib/tool-kit/math/decision-matrix";

/*
 * The results half of the decision-matrix calculator: ranking, margin, the working for
 * the winner and the sensitivity check. Every word comes from the widget's strings.
 */

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

/** Below this share of the scale, a lead is within the noise of the scoring. */
const CLOSE_CALL = 0.05;

function names(
  indexes: readonly number[],
  options: readonly string[],
  join: string
) {
  return indexes.map((i) => options[i]).join(join);
}

function tippingText(
  check: WeightCheck,
  options: readonly string[],
  strings: DecisionStrings,
  fmt: Format
): string {
  if (check.tippingChange === null || check.tippingOption === null) {
    return strings.noFlip;
  }
  const values = {
    percent: fmt(Math.abs(check.tippingChange) * 100, {
      maximumFractionDigits: 1,
    }),
    option: options[check.tippingOption],
  };
  return t(
    check.tippingChange > 0 ? strings.tippingUp : strings.tippingDown,
    values
  );
}

function Sensitivity({
  result,
  criteria,
  options,
  strings,
}: {
  result: DecisionResult;
  criteria: readonly string[];
  options: readonly string[];
  strings: DecisionStrings;
}) {
  const fmt = useToolNumber();
  if (result.checks === null) {
    return <Notice>{strings.tieNoCheck}</Notice>;
  }
  const flips = result.checks
    .filter((c) => c.changes)
    .map((c) => criteria[c.criterion]);
  return (
    <>
      <p className="text-muted-foreground text-sm">
        {strings.sensitivityIntro}
      </p>
      <DataTable
        head={[
          strings.criterionColumn,
          strings.weightColumn,
          strings.downColumn,
          strings.upColumn,
          strings.tippingColumn,
        ]}
        rows={result.checks.map((check) => [
          criteria[check.criterion],
          `${fmt(result.normalizedWeights[check.criterion] * 100, { maximumFractionDigits: 1 })}%`,
          names(check.downWinners, options, strings.listJoin),
          names(check.upWinners, options, strings.listJoin),
          tippingText(check, options, strings, fmt),
        ])}
      />
      <Notice>
        {result.stable
          ? strings.stableSummary
          : t(strings.sensitiveSummary, {
              criteria: flips.join(strings.listJoin),
            })}
      </Notice>
    </>
  );
}

function MarginNote({
  result,
  options,
  max,
  strings,
}: {
  result: DecisionResult;
  options: readonly string[];
  max: number;
  strings: DecisionStrings;
}) {
  const fmt = useToolNumber();
  if (result.winners.length > 1) {
    return (
      <Notice>
        {t(strings.tieNote, {
          options: names(result.winners, options, strings.listJoin),
        })}
      </Notice>
    );
  }
  if (result.margin === null) {
    return null;
  }
  const share = result.margin / max;
  return (
    <Notice>
      {t(strings.marginNote, {
        winner: options[result.ranking[0].option],
        runnerUp: options[result.ranking[1].option],
        margin: fmt(result.margin, { maximumFractionDigits: 2 }),
        share: fmt(share * 100, { maximumFractionDigits: 1 }),
      })}{" "}
      {share < CLOSE_CALL ? strings.closeCall : null}
    </Notice>
  );
}

export function DecisionResults({
  criteria,
  options,
  weights,
  scores,
  max,
  percentMode,
  strings,
}: {
  criteria: readonly string[];
  options: readonly string[];
  weights: readonly number[];
  scores: readonly (readonly number[])[];
  max: number;
  percentMode: boolean;
  strings: DecisionStrings;
}) {
  const fmt = useToolNumber();
  const result = analyseDecision({ weights, scores });
  if (result === null) {
    return null;
  }
  const weightSum = weights.reduce((sum, w) => sum + w, 0);
  const leader = result.ranking[0].option;
  const two = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  let stability: string = strings.notApplicable;
  if (result.stable !== null) {
    stability = result.stable ? strings.stable : strings.sensitive;
  }
  const terms = result.normalizedWeights
    .map(
      (w, j) =>
        `${fmt(w, { maximumFractionDigits: 3 })} × ${fmt(scores[leader][j])}`
    )
    .join(" + ");
  return (
    <>
      {percentMode && Math.abs(weightSum - 100) > 1e-9 ? (
        <Notice>
          {t(strings.percentMismatch, {
            total: fmt(weightSum, { maximumFractionDigits: 2 }),
          })}
        </Notice>
      ) : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.winner}
          value={names(result.winners, options, strings.listJoin)}
        />
        <StatCard
          label={strings.margin}
          value={
            result.margin === null ? strings.notApplicable : two(result.margin)
          }
        />
        <StatCard label={strings.stability} value={stability} />
      </StatGrid>
      <DataTable
        head={[
          strings.rankColumn,
          strings.optionColumn,
          t(strings.scoreColumn, { max: fmt(max) }),
          strings.percentColumn,
        ]}
        rows={result.ranking.map((row) => [
          fmt(row.rank),
          options[row.option],
          two(row.total),
          `${fmt((row.total / max) * 100, { maximumFractionDigits: 1 })}%`,
        ])}
      />
      <MarginNote
        max={max}
        options={options}
        result={result}
        strings={strings}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <p className="break-words rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        {options[leader]}: {terms} = {two(result.totals[leader])}
      </p>
      <h3 className={WIDGET_H3}>{strings.sensitivityHeading}</h3>
      <Sensitivity
        criteria={criteria}
        options={options}
        result={result}
        strings={strings}
      />
    </>
  );
}
